import React from "react";
import { Col, FormControl, FormGroup, FormLabel, Row } from "react-bootstrap";
import { ErrorMessages, InputBox, SavedMessage } from "../../components";
import { AccountInterface, ApiHelper, NavigationHelper, UserHelper, UserInterface } from "../../helpers";

interface Props {

}

export const AccountSettings: React.FC<Props> = props => {

  const [user, setUser] = React.useState<UserInterface>(null);
  const [account, setAccount] = React.useState<AccountInterface>(null);

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [verifyPassword, setVerifyPassword] = React.useState("");
  const [accountName, setAccountName] = React.useState("");
  const [address1, setAddress1] = React.useState("");
  const [address2, setAddress2] = React.useState("");
  const [city, setCity] = React.useState("");
  const [state, setState] = React.useState("");
  const [zip, setZip] = React.useState("");
  const [country, setCountry] = React.useState("");

  const [status, setStatus] = React.useState("");
  const [savedKey, setSavedKey] = React.useState("");
  const [errors, setErrors] = React.useState<string[]>([]);

  const init = () => {
    ApiHelper.get("/users/" + UserHelper.user.id).then((data: UserInterface) => { setUser(data); setEmail(data.email) });
    ApiHelper.get("/accounts/" + UserHelper.currentDepartment.accountId).then((data: AccountInterface) => {
      setAccount(data);
      setAccountName(data.name)
      setAddress1(data.address1);
      setAddress2(data.address2);
      setCity(data.city);
      setState(data.state);
      setZip(data.zip);
      setCountry(data.country);
      setStatus(data.status);
    });
  }

  React.useEffect(init, [])


  const handleChange = (e: any) => {
    let val = e.currentTarget.value;
    switch (e.currentTarget.id) {
      case "accountName": setAccountName(val); break;
      case "address1": setAddress1(val); break;
      case "address2": setAddress2(val); break;
      case "city": setCity(val); break;
      case "state": setState(val); break;
      case "zip": setZip(val); break;
      case "country": setCountry(val); break;
      case "email": setEmail(val); break;
      case "password": setPassword(val); break;
      case "verifyPassword": setVerifyPassword(val); break;
      case "status": setStatus(val); break;
    }
  }

  const validate = () => {
    const emailValidator = /\S+@\S+\.\S+/;
    const result: string[] = [];
    if (accountName === "") result.push("Account name cannot be blank.");
    if (email === "") result.push("Email cannot be blank.");
    else if (!emailValidator.test(email)) result.push("Invalid email address.");
    if (password !== "") {
      if (password !== verifyPassword) result.push("Passwords do not match");
    }
    setErrors(result);
    return result;
  }


  const handleSave = () => {
    const errorList = validate();
    if (errorList.length === 0) {
      if (accountName !== account.name || address1 !== account.address1 || address2 !== account.address2 || city !== account.city || state !== account.state || zip !== account.zip || country !== account.country) {
        ApiHelper.post("/accounts/updateDetails", { name: accountName, address1, address2, city, state, zip, country }).then(() => {
          setSavedKey(NavigationHelper.getRefreshKey());
        });
      }

      if (status !== account.status) {
        ApiHelper.post("/accounts/updateStatus", { status: status }).then(() => {
          setSavedKey(NavigationHelper.getRefreshKey());
        });
      }

      if (email !== user.email) {
        ApiHelper.post("/users/updateEmail", { email: email }).then(() => {
          setSavedKey(NavigationHelper.getRefreshKey());
        });
      }

      if (password !== "") {
        ApiHelper.post("/users/updatePassword", { password: password }).then(() => {
          setSavedKey(NavigationHelper.getRefreshKey());
        });
      }
    }
  }

  const getAdminSettings = () => {
    if (UserHelper.checkAccess("admin")) {
      return (
        <FormGroup className="mb-3">
          <FormLabel>Status</FormLabel>
          <FormControl data-testid="account-status-select" as="select" id="status" type="password" value={status} onChange={handleChange} >
            <option value="preview">Preview</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </FormControl>
        </FormGroup>
      )
    }
  }


  return (
    <InputBox headerText="Account Settings" headerIcon="fas fa-cog" saveFunction={handleSave} >
      <SavedMessage refreshKey={savedKey} />
      <ErrorMessages errors={errors} />
      <FormGroup className="mb-3">
        <FormLabel>Account Name</FormLabel>
        <FormControl data-testid="account-name-input" id="accountName" value={accountName} onChange={handleChange} />
      </FormGroup>
      <FormGroup className="mb-3">
        <FormLabel>Address Line 1</FormLabel>
        <FormControl data-testid="address1-input" id="address1" value={address1} onChange={handleChange} />
      </FormGroup>
      <FormGroup className="mb-3">
        <FormLabel>Address Line 2</FormLabel>
        <FormControl data-testid="address2-input" id="address2" value={address2} onChange={handleChange} />
      </FormGroup>
      <Row>
        <Col>
          <FormGroup className="mb-3">
            <FormLabel>City</FormLabel>
            <FormControl data-testid="city-input" id="city" value={city} onChange={handleChange} />
          </FormGroup>
        </Col>
        <Col>
          <FormGroup className="mb-3">
            <FormLabel>State</FormLabel>
            <FormControl data-testid="state-input" id="state" value={state} onChange={handleChange} />
          </FormGroup>
        </Col>
        <Col>
          <FormGroup className="mb-3">
            <FormLabel>Zip</FormLabel>
            <FormControl data-testid="zip-input" id="zip" value={zip} onChange={handleChange} />
          </FormGroup>
        </Col>
      </Row>
      <FormGroup className="mb-3">
        <FormLabel>Country</FormLabel>
        <FormControl data-testid="country-input" id="country" value={country} onChange={handleChange} />
      </FormGroup>
      <hr />
      <FormGroup className="mb-3">
        <FormLabel>Email</FormLabel>
        <FormControl data-testid="settings-email-input" id="email" value={email} onChange={handleChange} />
      </FormGroup>
      <Row>
        <Col>
          <FormGroup className="mb-3">
            <FormLabel>Password</FormLabel>
            <FormControl data-testid="settings-password-input" id="password" type="password" value={password} onChange={handleChange} />
          </FormGroup>
        </Col>
        <Col>
          <FormGroup className="mb-3">
            <FormLabel>Confirm Password</FormLabel>
            <FormControl data-testid="verify-password-input" id="verifyPassword" type="password" value={verifyPassword} onChange={handleChange} />
          </FormGroup>
        </Col>
      </Row>
      {getAdminSettings()}
    </InputBox>
  );

};